import type { FC } from 'react';
import React from 'react';
import cn from 'classnames';
import { TextDesktop as Text } from '@alfa-bank/site-text-v2/desktop';
import { Grid } from '@alfa-bank/site-grid-universal';
import {
    getSymbolForPrice,
    getProfitabilityByPeriod,
} from '@alfa-bank/site-invest-emitents-common/helpers';
import {
    ASSET_NAME_BOND,
    ASSET_NAME_STOCK,
    EMPTY_VALUE_TEXT,
    TITLE_DYNAMICS,
    TITLE_PROFITABILITY,
} from '@alfa-bank/site-invest-emitents-common/constants';
import { HintDesktop as Hint } from '@alfa-bank/site-invest-mediaportal-common/components/hint-desktop';
import {
    PriceRenderer,
    ProfitabilityRenderer,
} from '@alfa-bank/site-invest-emitents-common/components/renderer';
import { getTrendStatus } from '@alfa-bank/site-invest-common/src/helpers/utils/get-trend-status';
import { TREND_STATUS } from '@alfa-bank/site-invest-common/src/constants';
import { PROFITABILITY_TYPE_DAY } from '@alfa-bank/site-invest-mediaportal-common/constants';

import styles from '../styles/desktop.css';
import type { TDynamics } from '../models';

import { HintDynamicsContentDesktop as HintDynamicsContent } from './hint-content/desktop';

export const DynamicsDesktop: FC<TDynamics> = ({ asset }) => {
    const { assetType, currency } = asset;
    const { profitability, profitabilityPercent } = getProfitabilityByPeriod(
        PROFITABILITY_TYPE_DAY,
        asset.profitabilities ?? []
    );

    const isProfitabilityVisible = assetType.code === ASSET_NAME_STOCK && !!profitability;

    const symbol = getSymbolForPrice({ assetType: assetType.code, symbol: currency.symbol });

    const trend = getTrendStatus(profitabilityPercent);
    const profitabilityClassNames = cn(styles.external, styles.text, {
        [styles.positive]: trend === TREND_STATUS.positive,
        [styles.negative]: trend === TREND_STATUS.negative,
        [styles.neutral]: trend === TREND_STATUS.neutral,
    });

    return (
        <>
            <Grid
                alignment="center"
                colAutoWidth
                collapse
                columnsMargin={4}
                justify="start"
                styler={{ marginBottom: 8 }}
            >
                <Text
                    className={cn(styles.external, styles.nowrap)}
                    kind="secondary"
                    styler={{ margin: 0 }}
                    view="primary-small"
                >
                    {assetType.code === ASSET_NAME_BOND ? TITLE_PROFITABILITY : TITLE_DYNAMICS}
                </Text>
                <Hint
                    content={<HintDynamicsContent assetTypeCode={assetType.code} />}
                    iconClassName={cn(styles.external, styles.hintIcon)}
                    restrictPopoverWidth={false}
                />
            </Grid>
            <Text styler={{ margin: 0 }} view="xsmall">
                <ProfitabilityRenderer
                    fallbackValue={EMPTY_VALUE_TEXT}
                    value={profitabilityPercent}
                />
                {isProfitabilityVisible && (
                    <>
                        <span className={profitabilityClassNames}>{' ('}</span>
                        <PriceRenderer
                            className={profitabilityClassNames}
                            fallbackValue={EMPTY_VALUE_TEXT}
                            symbol={symbol}
                            value={profitability}
                        />
                        <span className={profitabilityClassNames}>{')'}</span>
                    </>
                )}
            </Text>
        </>
    );
};
